import { Link } from "react-router-dom";
import { useInfiniteQuery, useMutation, useQueryClient } from "@project/ui";
import { Button } from "@project/ui";
import { Icon, formatShort } from "@project/ui";
import { DashboardBackLink } from "@apps/dashboard/components/layout/DashboardBackLink";
import { DASHBOARD_PAGE_CONTAINER } from "@apps/dashboard/components/layout/dashboard-page-layout";
import { useI18n } from "@apps/dashboard/contexts/I18nContext";
import {
  fetchNotifications,
  homeExpenseQueryKeys,
  markAllNotificationsRead,
  markNotificationRead,
} from "@apps/dashboard/lib/home-expense-api";
import { dashboardPaths } from "@apps/dashboard/lib/routes";
import type { NotificationInAppItem } from "@apps/dashboard/lib/home-expense-types";
import { useCallback } from "react";

function severityRing(severity: string): string {
  if (severity === "critical") return "ring-2 ring-error/40";
  if (severity === "warning") return "ring-2 ring-tertiary/40";
  if (severity === "success") return "ring-2 ring-secondary/40";
  return "ring-1 ring-outline-variant/20";
}

export default function NotificationsInboxPage(): JSX.Element {
  const { t, locale } = useI18n();
  const qc = useQueryClient();

  const invalidateNotificationQueries = useCallback(() => {
    void qc.invalidateQueries({ queryKey: homeExpenseQueryKeys.notificationsInbox(locale) });
    void qc.invalidateQueries({ queryKey: homeExpenseQueryKeys.notificationUnread(locale) });
  }, [qc, locale]);

  const inboxQuery = useInfiniteQuery({
    queryKey: [...homeExpenseQueryKeys.notificationsInbox(locale), "paged"],
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) =>
      fetchNotifications(locale, { limit: 15, cursor: pageParam ?? undefined }),
    getNextPageParam: (last) => last.nextCursor,
  });

  const markReadMut = useMutation({
    mutationFn: (id: string) => markNotificationRead(locale, id),
    onSuccess: () => {
      invalidateNotificationQueries();
    },
  });

  const markAllMut = useMutation({
    mutationFn: () => markAllNotificationsRead(locale),
    onSuccess: () => {
      invalidateNotificationQueries();
    },
  });

  const items: NotificationInAppItem[] =
    inboxQuery.data?.pages.flatMap((p) => p.items) ?? [];

  return (
    <div className={DASHBOARD_PAGE_CONTAINER}>
      <DashboardBackLink to={dashboardPaths.home}>{t("dashboardUi.searchPage.backHome")}</DashboardBackLink>
      <h1 className="font-headline text-2xl font-bold tracking-tight text-primary">
        {t("dashboardUi.pages.notifications")}
      </h1>
      <p className="mt-2 font-body text-body-md text-on-surface-variant">
        {t("dashboardUi.notificationsPage.subtitle")}
      </p>

      <section className="mt-10">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <h2 className="font-headline text-sm font-bold uppercase tracking-wide text-primary">
            {t("dashboardUi.notificationsPage.inboxTitle")}
          </h2>
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="font-headline text-xs font-semibold"
              disabled={markAllMut.isPending || items.length === 0}
              onClick={() => markAllMut.mutate()}
            >
              {t("dashboardUi.notificationsPage.markAllRead")}
            </Button>
          </div>
        </div>

        {inboxQuery.isLoading ? (
          <p className="font-body text-sm text-on-surface-variant">{t("common.loading")}</p>
        ) : items.length === 0 ? (
          <div className="flex flex-col items-center rounded-2xl border border-dashed border-outline-variant/30 bg-surface-container-lowest/50 px-8 py-16 text-center">
            <div className="mb-4 flex size-16 items-center justify-center rounded-full bg-tertiary/10 text-tertiary">
              <Icon name="Bell" className="size-8" strokeWidth={1.25} />
            </div>
            <h3 className="font-headline text-lg font-bold text-on-surface">
              {t("dashboardUi.notificationsPage.emptyTitle")}
            </h3>
            <p className="mt-2 max-w-sm font-body text-sm text-on-surface-variant">
              {t("dashboardUi.notificationsPage.emptyHint")}
            </p>
          </div>
        ) : (
          <ul className="flex flex-col gap-3">
            {items.map((n) => (
              <li
                key={n.id}
                className={`rounded-2xl bg-surface-container-low p-4 ${severityRing(n.severity)}`}
              >
                <div className="flex flex-col gap-2">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="font-headline text-sm font-bold text-on-surface">
                        {t(n.titleKey, n.params)}
                      </p>
                      <p className="mt-1 font-body text-sm text-on-surface-variant">
                        {t(n.bodyKey, n.params)}
                      </p>
                      <p className="mt-2 font-body text-xs text-on-surface-variant/80">
                        {formatShort(n.createdAt)}
                        {n.readAt == null ? ` · ${t("dashboardUi.notificationsPage.unreadLabel")}` : ""}
                      </p>
                    </div>
                    {n.readAt == null ? (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="shrink-0 font-headline text-xs"
                        disabled={markReadMut.isPending}
                        onClick={() => markReadMut.mutate(n.id)}
                      >
                        {t("dashboardUi.notificationsPage.markOneRead")}
                      </Button>
                    ) : null}
                  </div>
                  {n.actionPath ? (
                    <Link
                      to={n.actionPath}
                      className="font-body text-sm font-semibold text-primary hover:underline"
                    >
                      {t("dashboardUi.notificationsPage.viewAction")}
                    </Link>
                  ) : null}
                </div>
              </li>
            ))}
          </ul>
        )}
        {inboxQuery.hasNextPage ? (
          <div className="mt-4 flex justify-center">
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="font-headline font-semibold"
              disabled={inboxQuery.isFetchingNextPage}
              onClick={() => void inboxQuery.fetchNextPage()}
            >
              {inboxQuery.isFetchingNextPage ? t("common.loading") : t("dashboardUi.notificationsPage.loadMore")}
            </Button>
          </div>
        ) : null}
      </section>
    </div>
  );
}
